<template>
  <div
    v-click-outside="unselectTask"
    v-hotkey="shortcuts"
    class="planner-footer-task-list"
    :class="{
      isDraggingTask,
      isOutsideDropZone,
      past: isPast,
    }"
    @click.self="unselectTask"
  >
    <BaseSkeletonList v-if="isLoading" :size="5" is-compact />
    <div v-else class="list">
      <DraggableContainer
        :id="dayIndex"
        ref="taskListEl"
        tag="div"
        class="itemslist"
      >
        <AddTaskButton
          v-if="!isPast"
          key="placeholder"
          class="task-button"
          size="compact"
          :add-task-action="{ type: 'task-scheduling', date: date }"
          :post-validate-action="selectTaskById"
          :metrics-source="'planner/footer'"
          @focus="unselectTask"
        />
        <!-- NB: no `:key` on the Suspense — keying it on the date used to
        remount the whole column on every period change (skeleton flash +
        refetch of every row), once when the new week's batch landed and
        once more when the debounced `dates` caught up. The rows are keyed
        by task id below, so the list reconciles incrementally instead. -->
        <Suspense v-if="taskIds.length > 0">
          <div class="footer-task-rows">
            <TaskInline
              v-for="id in orderedTodoIds"
              :key="id"
              :actions="footerTaskActions"
              :external-drag-class="draggableTaskClassName"
              :compact="true"
              :is-selected="id === selectedTaskId"
              :task-id="id"
              :with-actions="false"
              :tooltip-position="tooltipPosition"
              title-only
              @select="selectTaskById(id)"
              @set-selected-task-el="setSelectedTaskEl"
            />
            <TaskInline
              v-for="id in otherTaskIds"
              :key="id"
              :actions="footerTaskActions"
              :external-drag-class="draggableTaskClassName"
              :compact="true"
              :is-selected="id === selectedTaskId"
              :task-id="id"
              :with-actions="false"
              :tooltip-position="tooltipPosition"
              title-only
              @select="selectTaskById(id)"
              @set-selected-task-el="setSelectedTaskEl"
            />
          </div>
          <template #fallback>
            <Loader>
              <BaseSkeletonList :size="5" is-compact />
            </Loader>
          </template>
        </Suspense>
        <div class="unselect-area" @click="unselectTask" />
      </DraggableContainer>
    </div>
  </div>
</template>

<script lang="ts">
import { isBefore } from "date-fns/isBefore";
import type { Placement } from "tippy.js";
import { computed, defineComponent, PropType, ref, toRefs, watch } from "vue";
import { DraggableContainer } from "vue-shopify-draggable";

import type { Day, TaskId } from "@ctrl/controller-rpc";

import BaseSkeletonList from "@/components/base/BaseSkeletonList.vue";
import * as inject from "@/components/inject";
import Loader from "@/components/Loader.vue";
import {
  dayKey,
  personalSectionKey,
  useOrderableTaskList,
} from "@/components/tasks/ordering";
import { useReleaser } from "@/ref";

import AddTaskButton from "../AddTaskButton.vue";
import type { TaskActions } from "../task/types";
import { useTaskList } from "../task/useTaskList";
import TaskInline from "../TaskInline.vue";
import { draggableTaskClassName } from "./controllers/draggable";

// The footer columns are narrow: rows show only the checkbox and title — no
// space/project chip and no label/priority tags (skipping their graph
// queries), and `title-only` below also hides the context and recurrence
// pills.
const footerTaskActions: TaskActions = {
  drag: false,
  hierarchy: false,
  notes: false,
  open: true,
  postpone: false,
  recurrence: false,
  star: false,
  schedule: {
    able: false,
  },
  space: false,
  tags: false,
};

export default defineComponent({
  components: {
    Loader,
    AddTaskButton,
    BaseSkeletonList,
    DraggableContainer,
    TaskInline,
  },
  props: {
    date: {
      type: Date,
      required: true,
    },
    expanded: {
      type: Boolean,
      required: true,
    },
    dayIndex: {
      type: Number,
      required: true,
    },
    height: {
      type: Number,
      required: true,
    },
    isDraggingTask: {
      type: Boolean,
      required: true,
    },
    isOutsideDropZone: {
      type: Boolean,
      required: true,
    },
    day: {
      type: Object as PropType<Day>,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    tooltipPosition: {
      type: String as PropType<Placement>,
      required: true,
    },
  },
  emits: {
    isFootTaskListActive: (_: boolean) => true,
    unselectItems: () => true,
  },
  async setup(props, { emit }) {
    const releaser = useReleaser();
    const { date, day } = toRefs(props);
    const console = inject.consoleModal();
    const controller = inject.controller();
    const contextMenu = inject.contextMenu();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const profile = inject.profile();
    const today = inject.today();
    const todoTaskIds = computed<TaskId[]>(() => day.value?.todo ?? []);
    const otherTaskIds = computed<TaskId[]>(() => day.value?.other ?? []);
    // Same key as Agenda / sidebar Today — footer mirrors that ranking.
    const dayListKey = computed(() =>
      personalSectionKey(profile.value, dayKey(date.value))
    );
    const { orderedIdsReadonly, release: releaseOrdering } =
      useOrderableTaskList(dayListKey, todoTaskIds);
    releaser.defer(releaseOrdering);
    const orderedTodoIds = computed(() => orderedIdsReadonly.value);
    const taskIds = computed<TaskId[]>(() => [
      ...orderedTodoIds.value,
      ...otherTaskIds.value,
    ]);
    const isPast = computed(() => isBefore(date.value, today.value));
    const unselectItems = () => emit("unselectItems");
    const {
      selectedTaskId,
      selectTaskById,
      setSelectedTaskEl,
      shortcuts,
      taskListEl,
      unselectTask,
      release: releaseTaskList,
    } = useTaskList(
      taskIds,
      {
        controller,
        console,
        contextMenu,
        isModalShown,
        isInputFocused,
      },
      {
        unselectItems,
        isSelectable: ref({ withUp: false, withDown: false }),
      }
    );
    releaser.defer(releaseTaskList);
    watch(selectedTaskId, (v) => {
      if (v === null) emit("isFootTaskListActive", false);
      else emit("isFootTaskListActive", true);
    });
    return {
      draggableTaskClassName,
      footerTaskActions,
      isPast,
      orderedTodoIds,
      otherTaskIds,
      selectedTaskId,
      selectTaskById,
      setSelectedTaskEl,
      shortcuts,
      taskIds,
      taskListEl,
      unselectTask,
    };
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/itemslist.scss";

.planner-footer-task-list {
  height: 100%;
  padding: 1px 4px;
  box-sizing: border-box;
  border: 1px solid transparent;
  .base-skeleton-list {
    margin-top: 12px;
    margin-inline-start: 8px;
  }

  &.isDraggingTask:not(.isOutsideDropZone):not(.past) {
    border-radius: 3px;
    transition: background-color 0.2s ease;
    @include themed() {
      border: 1px dashed t($secondary);
      background-color: t($grey-05);
    }

    .task-button {
      pointer-events: none;
    }
  }

  .task-button {
    overflow: hidden;
    min-height: 31px;
  }

  .list {
    display: flex;
    flex-direction: column;
    height: 100%;
    .itemslist {
      flex: 1;
      overflow-y: auto;
      .unselect-area {
        display: flex;
        flex: 1;
      }
    }
  }

  .task-ghost {
    opacity: 0.5;
    @include themed() {
      background-color: t($grey-05);
    }
  }

  .loaders {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    row-gap: 12px;
  }

  .empty-button {
    pointer-events: none;
  }
}
</style>
